import { Capacitor } from "../_stubs/capacitorCore";

/** Backend wallet app slug — {@link docs/mobile-credit-rest-api.md} */
export const SLIDING_CREDIT_APP_CODE = "sliding_puzzle_quest";

/** Logged to `mo_credit_usage_logs.feature_used` when a premium puzzle is completed. */
export const SLIDING_CREDIT_FEATURE_PREMIUM_PUZZLE = "sliding_premium_puzzle";

/** RevenueCat offering package identifiers (default offering). */
export const CREDIT_PACKAGE_IDS = {
    masterpass: "masterpass",
    pocket: "pocket",
    pro: "pro",
};

/** Settings (and similar) show Pocket + Pro top-up when balance is below this. */
export const LOW_CREDIT_TOP_UP_THRESHOLD = 10;

/** App Store product ids — must match App Store Connect + backend YAML. */
export const CREDIT_PRODUCT_IDS = {
    masterpass: "com.pzl.sliding.quest.masterpass",
    pocket: "com.pzl.sliding.quest.pocket",
    pro: "com.pzl.sliding.quest.pro",
};

/** Credits granted per product (fallback when catalog API unavailable). */
export const CREDIT_AMOUNTS = {
    [CREDIT_PRODUCT_IDS.masterpass]: 650,
    [CREDIT_PRODUCT_IDS.pocket]: 50,
    [CREDIT_PRODUCT_IDS.pro]: 110,
};

/** App Store suggested CAD prices — offline/dev fallback only; UI prefers StoreKit `priceString`. */
export const CREDIT_SUGGESTED_PRICES_CAD = {
    [CREDIT_PACKAGE_IDS.pocket]: 1.99,
    [CREDIT_PACKAGE_IDS.pro]: 3.99,
    [CREDIT_PACKAGE_IDS.masterpass]: 19.99,
};

/** Display metadata for paywall / StoreKit (fallback when live price unavailable). */
export const CREDIT_PRODUCT_METADATA = {
    [CREDIT_PACKAGE_IDS.pocket]: {
        productId: CREDIT_PRODUCT_IDS.pocket,
        displayName: "Pocket Pack",
        description: "50 credits",
        credits: 50,
        priceCad: 1.99,
    },
    [CREDIT_PACKAGE_IDS.pro]: {
        productId: CREDIT_PRODUCT_IDS.pro,
        displayName: "Pro Pack",
        description: "110 credits",
        credits: 110,
        priceCad: 3.99,
    },
    [CREDIT_PACKAGE_IDS.masterpass]: {
        productId: CREDIT_PRODUCT_IDS.masterpass,
        displayName: "Master Pass",
        description: "650 credits",
        credits: 650,
        priceCad: 19.99,
    },
};

/** Hardcoded prices are for web/dev only — on native always prefer store `priceString` from RevenueCat. */
export function shouldUseSuggestedCreditPriceFallback() {
    try {
        return !Capacitor.isNativePlatform();
    } catch {
        return true;
    }
}

export function getSuggestedCreditPrice(packageKey, { currency = "CAD" } = {}) {
    const key = String(packageKey || "").trim().toLowerCase();
    const amount = CREDIT_SUGGESTED_PRICES_CAD[key];
    if (!Number.isFinite(amount)) {
        return null;
    }
    if (currency === "CAD" || currency === "USD") {
        return `$${amount.toFixed(2)}`;
    }
    return `${amount.toFixed(2)} ${currency}`;
}

export function resolveCreditDisplayPrice(packageKey, livePriceString, { allowCadFallback = true } = {}) {
    const live = typeof livePriceString === "string" ? livePriceString.trim() : "";
    if (live) {
        return live;
    }
    if (!allowCadFallback) {
        return null;
    }
    return getSuggestedCreditPrice(packageKey);
}

/** One-line purchase button label — pack name, store price, credits (each once). */
export function formatCreditPackButtonLabel(
    packageKey,
    livePriceString,
    creditsAwarded,
    { allowCadFallback = true } = {},
) {
    const key = String(packageKey || "").trim().toLowerCase();
    const meta = CREDIT_PRODUCT_METADATA[key];
    const title = meta?.displayName ?? "Credit pack";
    const price = resolveCreditDisplayPrice(key, livePriceString, { allowCadFallback });
    const credits =
        creditsAwarded ?? meta?.credits ?? creditsForProductId(CREDIT_PRODUCT_IDS[key]);
    if (price && credits) {
        return `${title} — ${price} (${credits} credits)`;
    }
    if (price) {
        return `${title} — ${price}`;
    }
    if (credits) {
        return `${title} (${credits} credits)`;
    }
    return title;
}

export const SLIDING_CREDIT_PRODUCT_GROUP = "appleSlidingPuzzleQuest";

export const ALL_CREDIT_PRODUCT_IDS = Object.values(CREDIT_PRODUCT_IDS);

export function creditsForProductId(productId) {
    const id = String(productId || "").trim();
    return CREDIT_AMOUNTS[id] ?? 0;
}

export function packageIdForProductId(productId) {
    const id = String(productId || "").trim();
    for (const [pkg, pid] of Object.entries(CREDIT_PRODUCT_IDS)) {
        if (pid === id) return pkg;
    }
    return null;
}
